import React from 'react'
import styled, { css } from 'styled-components'

import { Skeleton } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { DomUtil } from '@root/shared'

import BG from './assets/emoji-trending-preview-bg.inline.svg'

type Props = {
  currency: CurrencyModel
  previewUrl: string | undefined
}

const positionsDemo = [
  ["Icy's the Seal | ICYS", '+1232%', '🥇'],

  ['White Blood Cell | WBC', '+742%', '🥈'],

  ["Eliza's Ai Pet | EA85Z", '+1540%', ''],
  ['Santa Koala | SANTA', '+354% ', ''],
  ['JUSTICE FOR DOG  | MAXI', '+418% ', ''],
]

export const EmojiTrendingPreview: React.FC<Props> = ({
  currency,
  previewUrl,
}) => {
  const premiumPosition = [
    `${currency.title} | ${currency.ticker}`,
    '+99999%',
    '🚀🥇',
  ]

  const renderPosition = (position: Array<string>) => (
    <>
      <Link>{position[0]}</Link> <Sep>|</Sep> {position[1]}{' '}
      <Emoji>{position[2]}</Emoji>
    </>
  )

  return (
    <Root>
      <Overlay>
        <FooterShadow />

        <Message>
          <Name>SOL TRENDING</Name>
          <Heading>
            SOL TRENDING <Sep>|</Sep> <b>Disclaimer</b>
          </Heading>
          <Premium>
            {previewUrl ? (
              <EmojiPreview
                role="img"
                style={{ backgroundImage: DomUtil.cssUrl(previewUrl) }}
              />
            ) : (
              <Skeleton
                css={css`
                  margin-inline: 4px 2px;
                `}
                width={18}
                height={18}
                variant="circular"
              />
            )}
            {renderPosition(premiumPosition)}
          </Premium>

          {positionsDemo.map((line, idx) => (
            <Position key={idx} $idx={idx}>
              <Icon size={14} currency={CurrencyModel.Sol} />
              {renderPosition(line)}
            </Position>
          ))}
        </Message>
      </Overlay>
    </Root>
  )
}

const Root = styled.div`
  background: linear-gradient(
    120deg,
    var(--background-9) 0%,
    var(--subscriptionGradientFrom-9) 50%,
    var(--subscriptionGradientTo-9) 70%,
    var(--premium-9) 100%
  );

  font-family:
    Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue', sans-serif;

  height: 160px;

  overflow: hidden;

  border-radius: 20px;
`

const Overlay = styled.div`
  background: var(--appearanceA-5);
  background-image: ${DomUtil.cssUrl(BG)};

  position: relative;

  padding: 20px;
  place-content: center;
  display: grid;
`

const Message = styled.div`
  background: var(--background-1);
  color: var(--text-12);

  width: fit-content;
  border-radius: 20px;
  box-shadow: inset 0 0 0 1px rgba(255 255 255 / 5%);

  border: 8px;
  padding: 8px 0 16px 4px;
  padding-inline-end: 40px;
`

const Name = styled.div`
  color: var(--subscriptionGradientTo-9);

  font-size: 8px;
  font-weight: 400;
  padding-inline-start: 8px;
  margin-top: 4px;
  margin-bottom: 4px;
`

const Heading = styled.div`
  color: var(--text-12);

  font-size: 10px;
  font-weight: 400;
  margin-bottom: 8px;
  padding-inline-start: 8px;
  display: grid;
  grid-auto-flow: column;
  justify-content: start;

  b {
    font-weight: 400;
    color: var(--subscriptionGradientTo-9);
    text-decoration: underline;
  }
`

const Position = styled.div<{ $idx: number | 0 }>`
  margin-bottom: ${(props) => (props.$idx < 2 ? '6px' : '0px')};

  font-size: 10px;
  font-weight: 400;

  color: var(--subscriptionGradientTo-9);
  display: grid;
  grid-auto-flow: column;
  justify-content: start;
`

const Premium = styled.div`
  font-size: 10px;
  font-weight: 400;

  color: var(--subscriptionGradientTo-9);
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  justify-content: start;
  align-items: center;
  margin-bottom: 8px;
`

const Link = styled.a`
  text-decoration-line: underline;
`

const Emoji = styled.span`
  font-size: 12px;
  margin-left: 5px;
`

const Sep = styled.span`
  color: var(--text-10);
  opacity: 0.8;
  padding: 0 4px;
`

const Icon = styled(CurrencyIcon)`
  margin-inline: 5px;
`

const FooterShadow = styled.span`
  background-image: linear-gradient(
    to bottom,
    transparent,
    rgba(var(--background-1-rgb), 0.85)
  );
  position: absolute;
  width: 100%;
  height: 50px;
  top: 110px;
  z-index: 1;
  bottom: 0;
`

const EmojiPreview = styled.div`
  background-size: contain;
  background-position: center center;
  width: 18px;
  height: 18px;
  margin-inline-start: 4px;
  margin-inline-end: 2px;
`
